<template>
  <div class="hm-rooms-summary">
    <div
      v-for="(room, idx) in rooms"
      :key="room.id || idx"
      class="hm-room-summary__item"
    >
      <div class="hm-room-summary__left">
        <div class="hm-room-summary__topline">
          <div class="hm-room-summary__title">
            <span class="hm-room-summary__num">{{ idx + 1 }}.</span>
            <span class="hm-room-summary__name">{{ room.room_category && room.room_category.name }}</span>
          </div>

          <div class="hm-room-summary__dates">
            <CalendarIcon aria-hidden="true"/>
            <span class="hm-room-summary__date">{{ fmtDate(room.check_in) }}</span>
            <ArrowIcon class="hm-room-summary__arrow" aria-hidden="true"/>
            <span class="hm-room-summary__date">{{ fmtDate(room.check_out) }}</span>
          </div>
        </div>

        <div class="hm-room-summary__bottomline">
          <div class="hm-room-summary__group">
            <span class="hm-room-summary__label">Guests:</span>

            <HMChip v-if="room.accomodation_type?.age_assignment?.adult" color="#9AB992">
              {{ room.accomodation_type.age_assignment.adult }}
              <template #icon><AdultIcon aria-hidden="true" /></template>
            </HMChip>
            <HMChip v-if="room.accomodation_type?.age_assignment?.teenager" color="#76A9B4">
              {{ room.accomodation_type.age_assignment.teenager }}
              <template #icon><TeenagerIcon aria-hidden="true" /></template>
            </HMChip>
            <HMChip v-if="room.accomodation_type?.age_assignment?.child" color="#D493B0">
              {{ room.accomodation_type.age_assignment.child }}
              <template #icon><ChildIcon aria-hidden="true" /></template>
            </HMChip>
          </div>

          <div class="hm-room-summary__group">
            <span class="hm-room-summary__label">Nights:</span>
            <HMChip>
              {{ room.nights || '0' }}
              <template #icon><NightsIcon aria-hidden="true" /></template>
            </HMChip>
            <HMChip
              :color="mealPlan?.color"
              isMeal
            >
              {{ mealPlan?.name }}
            </HMChip>
          </div>
        </div>
      </div>

      <div class="hm-room-summary__right">
        <div class="hm-room-summary__count" :aria-label="`Rooms: ${ room.count }`">x{{ room.count }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import AdultIcon from '@/views/hotel-management/reservations/detail/icons/AdultIcon.vue'
import TeenagerIcon from '@/views/hotel-management/reservations/detail/icons/TeenagerIcon.vue'
import ChildIcon from '@/views/hotel-management/reservations/detail/icons/ChildIcon.vue'
import NightsIcon from '@/views/hotel-management/reservations/detail/icons/NightsIcon.vue'
import ArrowIcon from '@/views/hotel-management/reservations/detail/icons/ArrowIcon.vue'
import CalendarIcon from '@/views/hotel-management/reservations/detail/icons/CalendarIcon.vue'
import HMChip from '@/views/hotel-management/reservations/components/HMChip.vue'

export default {
  name: 'HMRoomSummary',
  components: {HMChip, AdultIcon, TeenagerIcon, ChildIcon, NightsIcon, ArrowIcon, CalendarIcon },
  props: {
    rooms: { type: Array, default: () => [] },
    mealPlan: { type: Object, default: () => null },
  },
  methods: {
    fmtDate(iso) {
      if (!iso) return ''
      const d = new Date(`${ iso }T00:00:00`)
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${ d.getDate() } ${ months[d.getMonth()] }, ${ d.getFullYear() }`
    },
  },
}
</script>

<style scoped>
.hm-rooms-summary {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.hm-room-summary__item {
  display: flex;
  align-items: stretch;
  border-radius: 16px;
  border: 1px solid #E9E9E9;
  background: #FFF;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.06);
  padding: 0;
}

.hm-room-summary__left {
  flex: 1 1 auto;
  padding: 20px 25px 16px;
}

.hm-room-summary__topline {
  display: grid;
  align-items: center;
  grid-template-columns:230px 1fr;
  gap: 18px;
}

.hm-room-summary__title {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.hm-room-summary__num {
  color: #000;
  font-family: 'MazzardSoftH', sans-serif;
  font-size: 16px;
  font-weight: 600;
}

.hm-room-summary__name {
  color: #000;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 15px;
  font-weight: 500;
}

.hm-room-summary__dates {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #000;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 15px;
  line-height: 1;
  font-weight: 500;
}

.hm-room-summary__bottomline {
  display: grid;
  grid-template-columns:210px 1fr;
  align-items: center;
  gap: 16px;
  margin-top: 10px;
  padding-left: 24px;
}

.hm-room-summary__group {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.hm-room-summary__label {
  color: #666;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: .5px;
  text-transform: uppercase;
  margin-right: 6px;
}

.hm-room-summary__meal {
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 14px;
  margin-left: 4px;
  border-radius: 8px;
  background: #F4F6F8;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 15px;
  line-height: 1;
}

.hm-room-summary__right {
  flex: 0 0 86px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.hm-room-summary__count {
  width: 46px;
  height: 40px;
  border-radius: 6px;
  background: #f6f6f6;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #383838;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 15px;
  font-weight: 500;
}
</style>
